import React, { useState, useEffect } from 'react';
import { Lock, Check } from 'lucide-react';
import styles from './form.module.css';
import ParticipantQuestionImage from './ParticipantQuestionImage';

const RatingScaleForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const minValue = Math.max(0, Number(content?.settings?.minValue) ?? 1);
    const maxValueRaw = Math.min(100, Number(content?.settings?.maxValue) ?? 10);
    const maxValue = maxValueRaw > minValue ? maxValueRaw : minValue + 1;
    const initial = userAnswer?.rating != null && userAnswer.rating >= minValue && userAnswer.rating <= maxValue
        ? Number(userAnswer.rating) : null;
    const [selected, setSelected] = useState(initial);

    useEffect(() => {
        if (userAnswer?.rating != null && userAnswer.rating >= minValue && userAnswer.rating <= maxValue) {
            setSelected(Number(userAnswer.rating));
        } else if (userAnswer == null) setSelected(null);
    }, [userAnswer, minValue, maxValue]);

    const hasSubmitted = userAnswer != null;
    const optionsDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const isUnchanged = hasSubmitted && selected === Number(userAnswer?.rating);
    const submitLabel = !hasSubmitted
        ? (isPreview ? 'Submit' : isLocked ? 'Voting Locked' : 'Submit')
        : !allowUpdateResponse || isUnchanged ? <>Submitted <Check size={14} strokeWidth={3} aria-hidden /></> : 'Update response';
    const submitDisabled = isPreview || isLocked || selected === null || (hasSubmitted && (!allowUpdateResponse || isUnchanged));

    const minLabel = content?.settings?.minLabel ?? '';
    const maxLabel = content?.settings?.maxLabel ?? '';

    const scaleNumbers = Array.from(
        { length: Math.max(0, maxValue - minValue + 1) },
        (_, i) => minValue + i
    );

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content?.question || 'Rate on the scale'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden /> Locked</span>}
            </div>

            {content?.settings?.description && content?.settings?.descText && (
                <div className={styles.description}>
                    {content.settings.descText}
                </div>
            )}

            {content?.settings?.imageUrl && (
                <ParticipantQuestionImage imageUrl={content?.settings?.imageUrl} alt="Question visual" />
            )}

            {/* Scale number buttons */}
            <div className={styles.scaleGrid} role="radiogroup" aria-label="Rating scale">
                {scaleNumbers.map((num) => {
                    const isSelected = selected === num;
                    return (
                        <button
                            key={num}
                            type="button"
                            className={`${styles.scaleNumber} ${isSelected ? styles.selected : ''}`}
                            onClick={() => !optionsDisabled && setSelected(num)}
                            disabled={optionsDisabled}
                            role="radio"
                            aria-checked={isSelected}
                            aria-label={`${num}${minLabel && num === minValue ? ` – ${minLabel}` : ''}${maxLabel && num === maxValue ? ` – ${maxLabel}` : ''}`}
                        >
                            {num}
                        </button>
                    );
                })}
            </div>

            {/* Endpoint labels */}
            {(minLabel || maxLabel) && (
                <div className={styles.scaleLabelsRow}>
                    <span>{minLabel}</span>
                    <span>{maxLabel}</span>
                </div>
            )}

            <button
                className={styles.submitBtn}
                onClick={() => !optionsDisabled && onSubmit({ rating: selected })}
                disabled={submitDisabled}
            >
                {submitLabel}
            </button>
        </div>
    );
};

export default RatingScaleForm;
